import { faCaretDown, faFilter, faSearch } from '@fortawesome/pro-solid-svg-icons';
import cs from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ActiveChannelFilters from 'features/messages/components/molecules/ActiveChannelFilters';
import {
  ChannelFilterValues,
  defaultChannelFilterValues,
} from 'features/messages/components/organisms/ChannelFilter/config';
import { useMessaging } from 'features/messages/useMessaging';
import { Field, Form, Formik, FormikHelpers } from 'formik';
import React, { useMemo, useState } from 'react';
import { Box, Button, Flex, Text } from 'shared/components/display';
import ButtonSelectInput from 'shared/components/molecules/ButtonSelectionInput';
import Dropdown from 'shared/components/molecules/Dropdown';
import TextInput from 'shared/components/TextInput';
import { MESSAGE_COPY } from 'shared/config/copy';
import Messaging from 'shared/messaging/Messaging';
import { ChatFilterOption, ChatFilterState, SetFilter } from 'shared/messaging/types';
import { BUTTON_VARIANTS } from 'shared/styles/button';
import { AccountManager } from 'shared/typings/account';
import styles, { searchBarStyles } from './styles';
import { faArrowRight } from '@fortawesome/pro-light-svg-icons';
import Tooltip from 'shared/components/atoms/Tooltip/NewTooltip';

const MENU_WIDTH = '252px';
interface Props {
  setFilter: SetFilter;
  filter: ChatFilterState;
}

const ChannelFilter: React.FC<Props> = ({ setFilter, filter }) => {
  const { getChannelIds, setActiveChannel, activeChannel } = useMessaging();
  const [display, setDisplay] = useState<'' | 'brands' | 'ams'>('');

  const updateActiveChannel = async (cIds: string[] = []) => {
    const activeChannelIsLoaded = activeChannel?.id && cIds.includes(activeChannel.id);
    if (!activeChannelIsLoaded && cIds.length > 0) {
      const channel = await Messaging.findAccountChannel(cIds[0]);
      setActiveChannel(channel);
    } else if (!activeChannelIsLoaded) {
      setActiveChannel(null);
    }
  };

  const getFilteredBrands = (
    values: ChannelFilterValues,
  ): {
    accountIds: number[];
    selectedBrands: { id: number; name: string }[];
    selectedAccountManager: AccountManager | undefined;
  } => {
    const selectedAccountManager = values.accountManagers.find((manager) => manager.selected)?.value;
    const selectedBrands = values.allBrands
      .filter((brand) => brand.selected)
      .map(({ value: { id, name } }) => ({ id, name }));
    // If only brands are selected, filter them
    // If only account manager is selected, filter brands by account manager
    // If both are selected, then options are limited to AM's brands and only filter on selected brands
    const hasAMandBrandSelected = selectedBrands.length > 0 && selectedAccountManager;
    const hasOnlyBrandsSelected = selectedBrands.length > 0 && !selectedAccountManager;
    if (hasAMandBrandSelected || hasOnlyBrandsSelected) {
      return {
        accountIds: selectedBrands.map(({ id }) => id),
        selectedBrands,
        selectedAccountManager,
      };
    } else {
      // only am selected
      return {
        accountIds:
          selectedAccountManager?.accounts.map(({ id }) => id).concat(selectedBrands.map(({ id }) => id)) || [],
        selectedBrands: [],
        selectedAccountManager,
      };
    }
  };

  const handleSubmit = (values: ChannelFilterValues) => {
    const { accountIds: allFilteredBrands, selectedAccountManager, selectedBrands } = getFilteredBrands(values);

    if (allFilteredBrands.length > 0 || values.search) {
      // fetch the channel ids that match the selected brands
      getChannelIds({ accountIds: allFilteredBrands, search: values.search }, (cIds: string[]) => {
        setFilter((prev) => {
          const noChannels = cIds.length === 0;
          if (noChannels) {
            delete prev.filter.id;
          }
          return {
            ...prev,
            // when submitting actually set the filter that is passed to stream chat
            filter: {
              ...prev.filter,
              // stream chat recommends to filter by channel id
              // rather than complex custom field filters
              id: { $in: cIds },
            },
            selectedAccountManager,
            allBrands: values.allBrands,
            accountManagers: values.accountManagers,
            selectedBrands,
            search: values.search || '',
          };
        });
        updateActiveChannel(cIds);
      });
    } else {
      setFilter((prev) => {
        // clear previous cid filter if no brands selected
        delete prev.filter.id;
        return {
          ...prev,
          selectedAccountManager,
          allBrands: values.allBrands,
          accountManagers: values.accountManagers,
          selectedBrands,
          filter: {
            ...prev.filter,
          },
          search: values.search || '',
        };
      });
      updateActiveChannel();
    }
  };

  const removeBrand =
    (values: ChannelFilterValues, formikHelpers: FormikHelpers<ChannelFilterValues>) => (accountId: number) => {
      const updatedBrands = values.allBrands.map((brand) => {
        if (brand.value.id === accountId) return { ...brand, selected: false };
        return brand;
      });
      formikHelpers.setFieldValue('allBrands', updatedBrands);
      handleSubmit({ ...values, allBrands: updatedBrands });
    };

  const removeAccountManager =
    (values: ChannelFilterValues, formikHelpers: FormikHelpers<ChannelFilterValues>) => () => {
      const updatedAccountManagers = values.accountManagers.map((manager) => ({ ...manager, selected: false }));
      const updatedBrands = values.allBrands.map((brand) => ({ ...brand, hidden: false }));
      formikHelpers.setFieldValue('accountManagers', updatedAccountManagers);
      handleSubmit({ ...values, accountManagers: updatedAccountManagers, allBrands: updatedBrands });
    };

  const onSelectAccountManager =
    (values: ChannelFilterValues, formikHelpers: FormikHelpers<ChannelFilterValues>) =>
    (option: ChatFilterOption<AccountManager>) => {
      // when selecting an account manager, we only want to show brands that are assigned to them
      const { value, selected } = option;
      if (selected) {
        const accountIds = value.accounts.map(({ id }) => id);
        const updatedBrands = values.allBrands.map((brand) => ({
          ...brand,
          hidden: !accountIds.includes(brand.value.id),
          selected: false,
        }));
        formikHelpers.setFieldValue('allBrands', updatedBrands);
      } else {
        const updatedBrands = values.allBrands.map((brand) => ({ ...brand, hidden: false }));
        formikHelpers.setFieldValue('allBrands', updatedBrands);
      }
    };

  const onSubmitSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    // calculate selected accounts based on what is in filter state (not form state) meaning what has been submitted
    const { accountIds: allFilteredBrands } = getFilteredBrands(filter);
    // fetch the channel ids and update filter since we want search to update on change rather than on submit
    getChannelIds({ accountIds: allFilteredBrands, search: event.target.value }, (cIds: string[]) => {
      setFilter((prev) => {
        const noChannels = cIds.length === 0;
        if (noChannels) {
          delete prev.filter.id;
        }
        return {
          ...prev,
          // when submitting actually set the filter that is passed to stream chat
          filter: {
            ...prev.filter,
            // stream chat recommends to filter by channel id
            // rather than complex custom field filters
            id: { $in: cIds },
          },
          search: event.target.value || '',
        };
      });
      updateActiveChannel(cIds);
    });
  };

  const onEnter = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      onSubmitSearch(event as unknown as React.ChangeEvent<HTMLInputElement>);
    }
  };

  const initialValues: ChannelFilterValues = useMemo(() => {
    return {
      ...defaultChannelFilterValues,
      allBrands: filter.allBrands,
      accountManagers: filter.accountManagers,
      search: filter.search,
    };
  }, [filter.allBrands, filter.accountManagers, filter.search]);

  const hasActiveFilters = useMemo(() => {
    return filter.allBrands.some((brand) => brand.selected) || Boolean(filter.selectedAccountManager?.id);
  }, [filter.allBrands, filter.selectedAccountManager?.id]);

  return (
    <Formik<ChannelFilterValues>
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validateOnBlur={false}
      enableReinitialize={false}
    >
      {(formikHelpers) => (
        <Form>
          <Flex css={searchBarStyles} className="channel-filter__search-bar">
            <Text className="channel-filter__icon">
              <FontAwesomeIcon icon={faSearch} />
            </Text>
            <Field
              name="search"
              className="channel-filter__search-input"
              component={TextInput}
              placeholder={MESSAGE_COPY.BRAND_SEARCH_PLACEHOLDER}
              onKeyDown={onEnter}
            />
          </Flex>
          <Dropdown
            ClickComponent={
              <Flex
                css={styles}
                className={cs('channel-filter__click-component', {
                  'channel-filter__click-component--active': hasActiveFilters,
                })}
              >
                <Text className="channel-filter__filter-name">
                  <FontAwesomeIcon className="channel-filter__filter-icon" icon={faFilter} size="xs" />
                  Filter
                </Text>

                <FontAwesomeIcon className="channel-filter__click-icon" icon={faCaretDown} size="xs" />
              </Flex>
            }
            className="channel-filter"
            width={MENU_WIDTH}
            dontCloseOnChildClick
          >
            {display === '' && (
              <>
                <Text css={styles} className="channel-filter__filter-label">
                  Filter By
                </Text>
                <Button
                  css={styles}
                  disabled={filter.loading}
                  onClick={() => setDisplay('brands')}
                  className="channel-filter__button"
                  variant={BUTTON_VARIANTS.OPTION}
                >
                  Brands
                  <FontAwesomeIcon icon={faArrowRight} />
                </Button>
              </>
            )}
            {display === 'brands' && (
              <Box css={styles} className="channel-filter__submenu-wrapper">
                <Box className="channel-filter__submenu-scroll-area">
                  <Text className="channel-filter__filter-label">Brands</Text>
                  <Field
                    component={ButtonSelectInput}
                    name="allBrands"
                    searchPlaceholder="Search brands"
                    allowMultipleSelect
                    showSearch
                  />
                </Box>
                <SubDropdownMenuButtons
                  onCancel={() => setDisplay('')}
                  onSave={() => handleSubmit(formikHelpers.values)}
                />
              </Box>
            )}
            {display === '' && (
              <Tooltip
                content="You can only filter by one account manager at a time"
                disabled={!(Boolean(filter.selectedAccountManager?.id) || filter.loading)}
              >
                <Button
                  css={styles}
                  disabled={Boolean(filter.selectedAccountManager?.id) || filter.loading}
                  onClick={() => setDisplay('ams')}
                  className="channel-filter__button"
                  variant={BUTTON_VARIANTS.OPTION}
                >
                  Account Managers
                  <FontAwesomeIcon icon={faArrowRight} />
                </Button>
              </Tooltip>
            )}
            {display === 'ams' && (
              <Box css={styles} className="channel-filter__submenu-wrapper">
                <Box className="channel-filter__submenu-scroll-area">
                  <Text className="channel-filter__filter-label">Account Managers</Text>
                  <Field
                    component={ButtonSelectInput}
                    name="accountManagers"
                    onSelect={onSelectAccountManager(formikHelpers.values, formikHelpers)}
                  />
                </Box>
                <SubDropdownMenuButtons
                  onCancel={() => setDisplay('')}
                  onSave={() => handleSubmit(formikHelpers.values)}
                />
              </Box>
            )}
          </Dropdown>
          <ActiveChannelFilters
            removeBrand={removeBrand(formikHelpers.values, formikHelpers)}
            removeAccountManager={removeAccountManager(formikHelpers.values, formikHelpers)}
            filterState={filter}
          />
        </Form>
      )}
    </Formik>
  );
};

const SubDropdownMenuButtons: React.FC<{
  onCancel?: () => void;
  onSave: () => void;
}> = ({ onCancel, onSave }) => {
  const handleCancel = () => {
    if (onCancel) onCancel();
  };

  return (
    <Flex css={styles} className="channel-filter__submenu-buttons">
      <Button
        variant={BUTTON_VARIANTS.SECONDARY_SMALL}
        className="channel-filter__submenu-button"
        onClick={handleCancel}
        type="button"
      >
        Cancel
      </Button>
      <Button
        variant={BUTTON_VARIANTS.PRIMARY_SMALL}
        onClick={onSave}
        className="channel-filter__submenu-button"
        type="submit"
      >
        Save
      </Button>
    </Flex>
  );
};

export default ChannelFilter;
